<div class="step-container" *transloco="let t">
	<div class="content-instructions-container" [formGroup]="discoveryMethodFormGroup">
		<div class="message-to-user">{{ t('cloud.add-devices-dialog.claim-discovery-message') }}</div>

		<prism-radio-group
			formControlName="discoveryMethod"
			class="device-discovery-method-container"
			(valueChange)="onDeviceDiscoveryMethodSelection($event)"
		>
			<!-- For small devices, the QR code option goes first -->
			@if (isSmallDevice() && isQrCodeClaimingEnabled()) {
				<prism-radio-button [value]="'QrCode'" id="sh-add-devices-step1-find-qrcode">
					{{ t('cloud.add-devices-dialog.claim-method-qr-code') }}
				</prism-radio-button>
			}

			<prism-radio-button [value]="'MacSerialBatchFile'" id="sh-add-devices-step1-find-batchfile">
				{{ t('cloud.add-devices-dialog.claim-method-mac-serial-batch') }}
			</prism-radio-button>

			<div class="mac-serial-batch-file-container">
				<prism-file-input
					#fileInput
					id="sh-add-devices-choose-csv-file-button"
					data-testid="sh-add-devices-choose-csv-file-button"
					accept=".csv"
					[formGroup]="macSerialBatchFileFormGroup"
					[multiple]="false"
					[disabled]="currentDiscoveryMethod !== 'MacSerialBatchFile'"
					(valueChange)="onFileSelected($event)"
				>
					<span slot="label">{{ t('cloud.add-devices-dialog.upload-csv-file') }}</span>
					<div slot="file-name-placeholder" id="mac-serial-batch-file-name">
						@if (macSerialBatchFileFormGroup.controls.fileName.value) {
							{{ macSerialBatchFileFormGroup.controls.fileName.value }}
						} @else {
							{{ t('cloud.add-devices-dialog.choose-file-to-upload') }}
						}
					</div>
				</prism-file-input>
				<!-- If a file was specified, and there were errors, display to the user. There are two classes of errors:
					1 - file was so bad that it couldn't be parsed.
					2 - file could be parsed and had errors. in this case, we let the user download the batchfile
					    which is amended with the error details.
				 -->
				@if (
					macSerialBatchFileFormGroup.controls.fileName.value &&
					macSerialBatchFileFormGroup.controls.fileName.getError('error')
				) {
					<div class="batch-file-error-message-container">
						@if (batchFileValidationResults === null) {
							<span class="error-text">
								{{ macSerialBatchFileFormGroup.controls.fileName.getError('error') }}
							</span>
						} @else {
							<p>
								<prism-icon
									class="file-format-error-icon"
									icon="sh-error-outline"
									color="warn"
								></prism-icon>
							</p>
							<p>
								{{ t('cloud.add-devices-dialog.csv-file-format-error-msg-part-1') }}
								<a
									class="prism-link csv-file-template-link"
									id="sh-add-devices-download-amended-batch-file"
									tabindex="0"
									(click)="saveBatchFileWithErrors()"
									(keydown.enter)="saveBatchFileWithErrors()"
								>
									{{ t('cloud.add-devices-dialog.csv-file-format-error-msg-part-2') }}
								</a>
								{{ t('cloud.add-devices-dialog.csv-file-format-error-msg-part-3') }}
							</p>
						}
					</div>
				}

				<a
					class="prism-link csv-file-template-link"
					style="width: fit-content"
					id="sh-add-devices-csv-file-template-link"
					tabindex="0"
					(click)="saveCSVTemplateFile(templateUrl, templateSaveName)"
					(keydown.enter)="saveCSVTemplateFile(templateUrl, templateSaveName)"
				>
					{{ t('cloud.add-devices-dialog.csv-file-template') }}
				</a>
			</div>

			<prism-radio-button id="sh-add-devices-step1-find-single-device" [value]="'MacSerialSingleDevice'">
				{{ t('cloud.add-devices-dialog.claim-method-mac-serial') }}
			</prism-radio-button>

			<div class="mac-serial-single-device-container" [formGroup]="macSerialSingleDeviceFormGroup">
				<!-- TODO (bisem) Use Prism input for MAC address once we can do masking -->
				<!-- <prism-input
					id="sh-step1-single-device-mac-address"
					type="text"
					formControlName="macAddress"
					mask="AA:AA:AA:AA:AA:AA"
					[angularInput]="true"
					[isValid]="macSerialSingleDeviceFormGroup.controls.macAddress.valid"
				>
					@if (macSerialSingleDeviceFormGroup.controls.macAddress.invalid) {
						<span slot="error-text">
							{{ t('cloud.add-devices-dialog.enter-a-valid-mac-address') }}
						</span>
					}
				</prism-input> -->
				<mat-form-field class="mac-address">
					<input
						matInput
						type="text"
						mask="AA:AA:AA:AA:AA:AA"
						[patterns]="customMacSerialPattern"
						[showMaskTyped]="true"
						[dropSpecialCharacters]="false"
						[validation]="true"
						formControlName="macAddress"
						placeholder="{{ t('cloud.shared.tables.mac-address') }}"
						id="sh-step1-single-device-mac-address"
					/>
					<mat-error>{{ t('cloud.add-devices-dialog.enter-a-valid-mac-address') }}</mat-error>
				</mat-form-field>
				<div class="mac-serial-divider">|</div>
				<!-- TODO (bisem) Use Prism input for serial number once MAC address is converted -->
				<!-- <prism-input
					id="sh-step1-single-device-serial-number"
					type="text"
					formControlName="serialNumber"
					maxlength="36"
					(keyup)="macSerialSingleDeviceFormGroup.get('serialNumber')?.markAsTouched()"
				>
					<span slot="label">{{ t('cloud.shared.tables.serial-number') }}</span>
					@if (macSerialSingleDeviceFormGroup.controls.serialNumber.invalid) {
						<span slot="error-text">
							{{ t('cloud.add-devices-dialog.enter-a-valid-serial-number') }}
						</span>
					}
				</prism-input> -->
				<mat-form-field class="serial-number">
					<input
						matInput
						type="text"
						formControlName="serialNumber"
						placeholder="{{ t('cloud.shared.tables.serial-number') }}"
						id="sh-step1-single-device-serial-number"
						(focus)="onSerialNumberFocus()"
						(blur)="onSerialNumberBlur()"
						maxlength="36"
					/>
					@if (!serialNumberFocused()) {
						<mat-error>
							{{ t('cloud.add-devices-dialog.enter-a-valid-serial-number') }}
						</mat-error>
					}
				</mat-form-field>
			</div>

			<prism-radio-button id="sh-add-devices-step1-find-localnetwork" [value]="'LocalNetwork'">
				{{ t('cloud.add-devices-dialog.claim-method-local-network') }}
			</prism-radio-button>

			<!-- For other devices, the QR code option goes last -->
			@if (!isSmallDevice() && isQrCodeClaimingEnabled()) {
				<prism-radio-button [value]="'QrCode'" id="sh-add-devices-step1-find-qrcode">
					{{ t('cloud.add-devices-dialog.claim-method-qr-code') }}
				</prism-radio-button>
			}
		</prism-radio-group>
	</div>
	<img class="content-image" src="../assets/images/cloud_communication.png" alt="cloud communication" />
</div>
